// combatpro/views/EncounterView.js
//
// Combat PRO - EncounterView (Stage 33 shell; Stage 46 partial; Stage 65 full
// migration).
//
// Stage 33 left view-encounter as a preserved container shell. Stage 65 completes
// the migration of the legacy Encounter page (battletracker.html 3378-3545) into
// the React architecture, faithfully and without redesign:
//   - vi-top-3col:
//       1. vi-info-block "Encounter Balance & Threat Assessment" - static guidance
//          plus the two action blocks (the page's only handlers), which call the
//          existing CombatContext switchView to jump to view-players / view-monsters
//          (legacy vi_switchView, 3406 / 3410).
//       2. vi-party-config "System State" - static guidance plus the live 2x2 stat
//          grid, extracted as <EncounterSystemState/> (the #enc-stat-* block).
//       3. #encounterSection "Encounter Overview" - fully static guidance.
//   - vi-bottom-centered -> <EncounterSummary/> - the balance bar + the four live
//     metrics (#vi-balance-bar-wrap, 3510-3543).
//
// Only the two dynamic sections are extracted into components; the static info
// block and overview remain inline here, verbatim. The legacy hidden inputs
// (#partySize / #partyLevel / #partyHP) were imperative DOM shims that only fed the
// threat calculation via getElementById; they are dropped because the pure
// encounterEngine derives partySize / partyHP from state.players and treats
// partyLevel as the legacy constant 5 (the legacy never changed it). No new
// encounter functionality, no persistence, no navigation change. This view only
// reads switchView from context for the two action blocks; all metric state is read
// inside the two child components. Markup, ids, inline styles, and text reproduce
// the master spec verbatim. No DOM manipulation, no getElementById. No optional
// chaining. No em dash.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { DAMAGE_TYPES } from '../damage/damageTypes';
import EncounterSummary from '../panels/encounter/EncounterSummary';
import EncounterSystemState from '../panels/encounter/EncounterSystemState';
import EncounterOpsPanel from '../runtime/EncounterOpsPanel';
import { resolveMassTargets, SIDES, SIDE_LABELS } from '../runtime/dmOpsEngine';
import { familyArrayKey, activeFamilyOf } from '../encounter/encounterStoreEngine';

const actionEyebrow = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--cp-text-muted)', letterSpacing: '0.5px' };
const actionBody = { fontSize: '13px', fontWeight: 500, color: 'var(--cp-text-2)', marginTop: '4px', lineHeight: 1.4 };

function EncounterView() {
  const { switchView, state, massDamage, cloneMonster, resetAllData, healAllToMax } = useContext(CombatContext);

  // Stage 97 (Mass Damage): view-local modal state, mirroring the WeaponsView
  // picker pattern (vi-modal-overlay / vi-modal-box). UI state only - the runtime
  // (massDamage) and gameplay are owned by CombatContext. massSelected is the set
  // of checked monster indices (legacy .mass-damage-checkbox:checked). massAmount /
  // massDamageType are the legacy massDamageAmount / massDamageType inputs.
  // Stage 123 (P2): massSelectedPlayers is the set of checked PLAYER ids (the
  // legacy data-entity="player" checkboxes, 8623-8632).
  const [massOpen, setMassOpen] = useState(false);
  const [massSelected, setMassSelected] = useState([]);
  const [massSelectedPlayers, setMassSelectedPlayers] = useState([]);
  const [massAmount, setMassAmount] = useState('');
  const [massDamageType, setMassDamageType] = useState(DAMAGE_TYPES[0]);

  // The mass-damage target list: active monsters with maxHP > 0 (legacy
  // showMassDamageModal 8610-8612). Read from state. Stage 123 (P2): players are
  // no longer a deferred seam - all players are listed (the legacy player section
  // listed every viPlayers entry with no filter, 8624-8632).
  function massTargets() {
    var list = [];
    var limit = state.activeMonsterCount;
    if (limit === undefined || limit > state.monsters.length) { limit = state.monsters.length; }
    for (var i = 0; i < limit; i++) {
      var m = state.monsters[i];
      if (m && m.maxHP > 0) { list.push(i); }
    }
    return list;
  }

  function openMass() {
    setMassSelected([]);
    setMassSelectedPlayers([]);
    setMassAmount('');
    setMassDamageType(DAMAGE_TYPES[0]);
    setMassOpen(true);
  }

  function closeMass() {
    setMassOpen(false);
  }

  function toggleMassTarget(index) {
    setMassSelected(function (prev) {
      var has = false;
      for (var i = 0; i < prev.length; i++) { if (prev[i] === index) { has = true; break; } }
      if (has) { return prev.filter(function (x) { return x !== index; }); }
      return prev.concat([index]);
    });
  }

  // Stage 123 (P2): toggle a player id in the checked-players set (the legacy
  // player checkbox, data-player-id, 8628).
  function toggleMassPlayer(id) {
    setMassSelectedPlayers(function (prev) {
      var has = false;
      for (var i = 0; i < prev.length; i++) { if (prev[i] === id) { has = true; break; } }
      if (has) { return prev.filter(function (x) { return x !== id; }); }
      return prev.concat([id]);
    });
  }

  // All / None: the legacy buttons checked / unchecked EVERY .mass-damage-checkbox,
  // players included (8697-8698), so both lists are covered (Stage 123, P2).
  function massSelectAll() {
    setMassSelected(massTargets());
    setMassSelectedPlayers(state.players.map(function (p) { return p.id; }));
  }

  function massSelectNone() {
    setMassSelected([]);
    setMassSelectedPlayers([]);
  }

  // Mass/Group Operations: resolve the checked-target set from a
  // side/group/all-enemies scope using the pure resolveMassTargets resolver
  // (dmOpsEngine). Groups come from the active encounter slot's dmOps. The
  // resolved indices are intersected with the rendered target list (massTargets)
  // so a scope pick never selects a monster that is not shown as a checkbox row.
  // No gameplay here - selection is view-local state; massDamage owns the runtime.
  function massGroupsList() {
    var store = state.encounterStore;
    var ai = (store && typeof store.activeIndex === 'number') ? store.activeIndex : 0;
    var arr = store ? (store[familyArrayKey(activeFamilyOf(store))] || store.encounters) : null;
    var enc = arr ? arr[ai] : null;
    var dmOps = (enc && enc.dmOps) ? enc.dmOps : null;
    return (dmOps && dmOps.groups) ? dmOps.groups : [];
  }

  function selectByScope(scope, extra) {
    var sel = Object.assign({ scope: scope }, extra || {});
    var resolved = resolveMassTargets(state.monsters, sel);
    var valid = massTargets();
    var out = [];
    for (var i = 0; i < resolved.length; i++) {
      for (var j = 0; j < valid.length; j++) {
        if (valid[j] === resolved[i]) { out.push(resolved[i]); break; }
      }
    }
    setMassSelected(out);
  }

  // Apply (legacy applyMassDamage 8716): guard amount + selection (the legacy
  // checkbox guard 8727 covered monsters AND players), call the massDamage runtime
  // (engine resistance + HP commit + morale + single summary log all owned there;
  // Stage 123: checked player ids passed through - the raw-damage player clamp is
  // owned by the runtime), then close. No gameplay logic here.
  function applyMass() {
    var amount = parseInt(massAmount, 10);
    if (isNaN(amount) || amount <= 0) { closeMass(); return; }
    if (massSelected.length === 0 && massSelectedPlayers.length === 0) { closeMass(); return; }
    massDamage(massSelected, amount, massDamageType, massSelectedPlayers);
    closeMass();
  }

  function isChecked(index) {
    for (var i = 0; i < massSelected.length; i++) { if (massSelected[i] === index) { return true; } }
    return false;
  }

  // Stage 123 (P2): checked-state read for a player id.
  function isPlayerChecked(id) {
    for (var i = 0; i < massSelectedPlayers.length; i++) { if (massSelectedPlayers[i] === id) { return true; } }
    return false;
  }

  // Stage 98 (Clone Monster): view-local modal state, mirroring the mass-damage /
  // weapon-picker pattern. UI state only - the cloneMonster runtime owns the clone /
  // slot / log gameplay. cloneSource is the selected source index (legacy
  // cloneSourceIndex select); cloneQuantity is the copies input (legacy cloneQuantity,
  // 1..10). The source list is monsters-with-data (legacy 8789-8793).
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState('');
  const [cloneQuantity, setCloneQuantity] = useState('1');

  // Source candidates: active monsters with data (legacy showCloneMonsterModal
  // 8789-8793: selectedMonster || customName || maxHP > 0).
  function cloneSources() {
    var list = [];
    var limit = state.activeMonsterCount;
    if (limit === undefined || limit > state.monsters.length) { limit = state.monsters.length; }
    for (var i = 0; i < limit; i++) {
      var m = state.monsters[i];
      if (m && (m.selectedMonster || m.customName || m.maxHP > 0)) { list.push(i); }
    }
    return list;
  }

  function openClone() {
    setCloneSource('');
    setCloneQuantity('1');
    setCloneOpen(true);
  }

  function closeClone() {
    setCloneOpen(false);
  }

  // Clone (legacy cloneMonster 8844): guard a chosen source, call the cloneMonster
  // runtime (clone / slot allocation / active-switch / single log all owned there),
  // then close. The runtime's own guards (source/quantity/empty-slot/confirm) are
  // authoritative; the view only supplies the chosen source + quantity and closes.
  function applyClone() {
    var sourceIndex = parseInt(cloneSource, 10);
    if (isNaN(sourceIndex)) { return; }
    var quantity = parseInt(cloneQuantity, 10);
    if (isNaN(quantity)) { quantity = 1; }
    cloneMonster(sourceIndex, quantity);
    closeClone();
  }

  return (
    <div className="vi-view" id="view-encounter">
      <EncounterOpsPanel />
      <div className="vi-top-3col">
        <div className="vi-info-block">
          <div className="vi-info-block-title">⚔️ Encounter Balance & Threat Assessment</div>
          <div className="vi-info-block-body">
            <div className="vi-hint-step">
              <span className="vi-hint-num">1</span>
              <span className="vi-hint-text">
                <span className="vi-hint-primary">Add players and monsters</span>
                <span className="vi-hint-sub">Use the Players and Monsters sections to build your encounter. All combat data is tracked automatically once entities are added.</span>
              </span>
            </div>
            <div className="vi-hint-step">
              <span className="vi-hint-num">2</span>
              <span className="vi-hint-text">
                <span className="vi-hint-primary">System calculates balance</span>
                <span className="vi-hint-sub">The system continuously calculates encounter balance using total HP, active entities, and combat state. No manual configuration is required.</span>
              </span>
            </div>
            <div className="vi-hint-step">
              <span className="vi-hint-num">3</span>
              <span className="vi-hint-text">
                <span className="vi-hint-primary">Adjust encounter dynamically</span>
                <span className="vi-hint-sub">Add or remove monsters, or modify HP during combat to match your intended difficulty.</span>
              </span>
            </div>
            <div className="vi-hint-sub" style={{ marginTop: '6px' }}>This system replaces manual encounter setup by using real-time combat data to evaluate difficulty and balance automatically, allowing you to adjust players and monsters in real time as combat evolves.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '12px' }}>
              <div onClick={() => switchView('view-players')} className="vi-action-block">
                <div style={actionEyebrow}>🧙 Players</div>
                <div style={actionBody}>Manage party members, HP, and tracking</div>
              </div>
              <div onClick={() => switchView('view-monsters')} className="vi-action-block">
                <div style={actionEyebrow}>👹 Monsters</div>
                <div style={actionBody}>Add enemies, configure stats, and control combat</div>
              </div>
            </div>
            {/* Stage 97 (Mass Damage): the legacy toolbar control (battletracker.html
                5687: showMassDamageModal). Faithful React equivalent placed in the
                encounter controls. Opens the view-local mass-damage modal. */}
            <div style={{ marginTop: '10px' }}>
              <button type="button" onClick={openMass} className="btn-danger vi-btn vi-mass-btn">💥 Group Damage</button>
              {/* Stage 123 (Heal All): the legacy toolbar control (battletracker.html
                  5688: healAllToMax), adjacent to Group Damage (5687) in the legacy
                  toolbar. Calls the CombatContext healAllToMax runtime directly (the
                  confirm and the massHealToMaxResults engine commit are owned there). */}
              <button type="button" onClick={healAllToMax} className="vi-btn vi-mass-btn" style={{ marginLeft: '8px' }}>💚 Heal All</button>
              {/* Stage 98 (Clone Monster): the legacy toolbar control (battletracker.html
                  5691: showCloneMonsterModal), adjacent to Group Damage (5687) in the
                  legacy toolbar. Faithful React placement, immediately beside it. */}
              <button type="button" onClick={openClone} className="btn-warning vi-btn vi-mass-btn" style={{ marginLeft: '8px' }}>📋 Clone Monster</button>
              {/* Stage 123 (Reset All): the legacy toolbar control (battletracker.html
                  5692: resetAllData), adjacent to Clone Monster (5691) in the legacy
                  toolbar. Calls the CombatContext resetAllData runtime directly (the
                  double confirm and the createMonster factory reset are owned there). */}
              <button type="button" onClick={resetAllData} className="vi-btn vi-mass-btn" style={{ marginLeft: '8px' }}>🔄 Reset All</button>
            </div>
          </div>
        </div>
        <div className="vi-party-config">
          <div className="vi-party-config-header">
            <div className="vi-party-config-title">⚙️ System State</div>
          </div>
          <div className="vi-party-config-hint">
            <div className="vi-hint-step">
              <span className="vi-hint-num">1</span>
              <span className="vi-hint-text">
                <span className="vi-hint-primary">Players (auto tracked)</span>
                <span className="vi-hint-sub">Total players and their combined HP are calculated automatically from the Players system.</span>
              </span>
            </div>
            <div className="vi-hint-step">
              <span className="vi-hint-num">2</span>
              <span className="vi-hint-text">
                <span className="vi-hint-primary">Monsters (auto tracked)</span>
                <span className="vi-hint-sub">Active monsters and their remaining HP are tracked in real time during combat.</span>
              </span>
            </div>
            <div className="vi-hint-step">
              <span className="vi-hint-num">3</span>
              <span className="vi-hint-text">
                <span className="vi-hint-primary">Live encounter data</span>
                <span className="vi-hint-sub">All values update instantly as damage, healing, and actions occur.</span>
              </span>
            </div>
          </div>
          <div className="vi-party-config-form" style={{ marginTop: 'auto' }}>
            <EncounterSystemState />
          </div>
        </div>
        <div id="encounterSection" className="vi-encounter-header">
          <div className="vi-encounter-header-title">📊 Encounter Overview</div>
          <div className="vi-encounter-header-body">
            <div className="vi-enc-steps">
              <div className="vi-enc-step">
                <span className="vi-hint-num">1</span>
                <span className="vi-enc-step-text">
                  <span className="vi-enc-step-primary">Build your encounter</span>
                  <span className="vi-enc-step-sub">Add monsters and players to begin. The system immediately starts tracking balance.</span>
                </span>
              </div>
              <div className="vi-enc-step">
                <span className="vi-hint-num">2</span>
                <span className="vi-enc-step-text">
                  <span className="vi-enc-step-primary">Monitor live metrics</span>
                  <span className="vi-enc-step-sub">Watch total HP, active entities, and combat flow to understand encounter strength.</span>
                </span>
              </div>
              <div className="vi-enc-step">
                <span className="vi-hint-num">3</span>
                <span className="vi-enc-step-text">
                  <span className="vi-enc-step-primary">Adjust in real time</span>
                  <span className="vi-enc-step-sub">Modify monsters, HP, or actions during combat to maintain pacing and challenge.</span>
                </span>
              </div>
            </div>
            <div className="vi-hint-sub" style={{ marginTop: '6px' }}>Use the balance guidance below as a live reference while combat changes. If the encounter becomes too easy, add pressure with stronger enemies or additional threats. If it becomes too hard, reduce monster HP, remove enemies, or ease incoming damage.</div>
            <div className="vi-enc-decision">
              <div className="vi-enc-decision-box vi-enc-decision-easy" style={{ padding: '16px 14px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--cp-success)', letterSpacing: '0.8px', marginBottom: '8px' }}>Too Easy</div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--cp-success)', lineHeight: 1.4 }}>Add more or stronger monsters</div>
              </div>
              <div className="vi-enc-decision-box vi-enc-decision-hard" style={{ padding: '16px 14px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: '#FCA5A5', letterSpacing: '0.8px', marginBottom: '8px' }}>Too Hard</div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: '#FCA5A5', lineHeight: 1.4 }}>Remove monsters or reduce HP</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="vi-bottom-centered">
        <EncounterSummary />
      </div>

      {/* Stage 97 (Mass Damage Modal): faithful port of the legacy showMassDamageModal
          (battletracker.html 8607-8700) on the established vi-modal-overlay /
          vi-modal-box framework. Checkbox list of active monsters (maxHP > 0) with
          name + HP, All / None toggles, Damage Amount, Damage Type (canonical
          DAMAGE_TYPES - the legacy mass modal listed 13 incl. Psychic; the project's
          canonical source is the 12-entry damageTypes.js, reused here per spec).
          Cancel closes (legacy 8707); Apply calls the massDamage runtime then closes
          (legacy 8711-8712 -> applyMassDamage -> modal.remove). Stage 123 (P2): the
          legacy player checkboxes (8623-8632) are implemented - a "Players" divider
          (inline style verbatim, 8625) followed by one row per player (name +
          hp/maxHp + the legacy player glyph), feeding massSelectedPlayers. */}
      {massOpen ? (
        <div className="mass-damage-modal-overlay">
          <div className="vi-modal-overlay">
            <div className="vi-modal-box-lg">
              <h3 className="vi-modal-title">{'\ud83d\udca5 Apply Damage to Multiple Monsters'}</h3>

              <div className="vi-modal-field">
                <label className="vi-label-sm">Targets:</label>
                <div className="vi-mass-target-list">
                  {massTargets().map(function (index) {
                    var m = state.monsters[index];
                    var name = m.customName ? m.customName : (m.selectedMonster ? m.selectedMonster : ('Monster ' + (index + 1)));
                    var hp = m.currentHP + '/' + m.maxHP;
                    return (
                      <label className="vi-modal-checkbox-row" key={index}>
                        <input
                          type="checkbox"
                          className="mass-damage-checkbox vi-checkbox-lg"
                          checked={isChecked(index)}
                          onChange={(function (ix) { return function () { toggleMassTarget(ix); }; })(index)}
                        />
                        <span className="vi-flex-1"><strong>{name}</strong> ({hp} HP) 👹</span>
                      </label>
                    );
                  })}
                  {state.players.length > 0 ? (
                    <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--cp-text-muted)', margin: '10px 0 4px 0', paddingTop: '8px', borderTop: '1px solid var(--cp-border)' }}>Players</div>
                  ) : null}
                  {state.players.map(function (p) {
                    return (
                      <label className="vi-modal-checkbox-row" key={'p-' + p.id}>
                        <input
                          type="checkbox"
                          className="mass-damage-checkbox vi-checkbox-lg"
                          checked={isPlayerChecked(p.id)}
                          onChange={(function (pid) { return function () { toggleMassPlayer(pid); }; })(p.id)}
                        />
                        <span className="vi-flex-1"><strong>{p.name}</strong> ({p.hp}/{p.maxHp} HP) 🧙</span>
                      </label>
                    );
                  })}
                </div>
                <div className="vi-modal-actions vi-mass-scope" style={{ marginTop: '6px', flexWrap: 'wrap' }}>
                  <button type="button" onClick={massSelectAll} className="btn-info vi-btn vi-btn-primary vi-btn-sm">All</button>
                  <button type="button" onClick={massSelectNone} className="vi-btn vi-btn-primary vi-btn-sm">None</button>
                  <button type="button" onClick={function () { selectByScope('all-enemies', {}); }} className="vi-btn vi-btn-primary vi-btn-sm">Enemies</button>
                  <select className="vi-modal-select-input vi-mass-scope-select" value="" onChange={function (e) { if (e.target.value) { selectByScope('side', { side: e.target.value }); } }}>
                    <option value="">By side</option>
                    {SIDES.map(function (s) { return (<option key={s} value={s}>{SIDE_LABELS[s]}</option>); })}
                  </select>
                  {massGroupsList().length > 0 ? (
                    <select className="vi-modal-select-input vi-mass-scope-select" value="" onChange={function (e) { if (e.target.value) { selectByScope('group', { group: e.target.value }); } }}>
                      <option value="">By group</option>
                      {massGroupsList().map(function (g) { return (<option key={g.id} value={g.id}>{g.name ? g.name : 'Group'}</option>); })}
                    </select>
                  ) : null}
                </div>
              </div>

              <div className="vi-modal-field">
                <label className="vi-label-sm">Damage Amount:</label>
                <input
                  type="number"
                  id="massDamageAmount"
                  min="0"
                  placeholder="20"
                  className="vi-modal-large-input"
                  value={massAmount}
                  onChange={function (e) { setMassAmount(e.target.value); }}
                />
              </div>

              <div className="vi-modal-field">
                <label className="vi-label-sm">Damage Type:</label>
                <select
                  id="massDamageType"
                  className="vi-modal-select-input"
                  value={massDamageType}
                  onChange={function (e) { setMassDamageType(e.target.value); }}
                >
                  {DAMAGE_TYPES.map(function (dt) {
                    return <option key={dt} value={dt}>{dt}</option>;
                  })}
                </select>
              </div>

              <div className="vi-modal-actions">
                <button type="button" id="cancelMassDamage" onClick={closeMass} className="vi-btn">Cancel</button>
                <button type="button" id="applyMassDamageBtn" onClick={applyMass} className="btn-danger vi-btn">Apply Damage</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Stage 98 (Clone Monster Modal): faithful port of the legacy
          showCloneMonsterModal (battletracker.html 8786-8837), on the established
          vi-modal-overlay / vi-modal-box framework. Source dropdown (monsters with
          data, with a blank default), a copies input (1..10), Cancel / Clone. The
          slot allocation, name increment, active-switch and combat log are owned by
          the cloneMonster runtime; this modal only selects source + quantity. */}
      {cloneOpen ? (
        <div className="clone-monster-modal-overlay">
          <div className="vi-modal-overlay">
            <div className="vi-modal-box">
              <h3 className="vi-modal-title">{'\ud83d\udccb Clone Monster'}</h3>
              <p className="vi-modal-body">Select a monster and how many copies to create.</p>

              <div className="vi-modal-field">
                <label className="vi-label-sm">Monster to Clone:</label>
                <select
                  id="cloneSourceIndex"
                  className="vi-modal-select-input"
                  value={cloneSource}
                  onChange={function (e) { setCloneSource(e.target.value); }}
                >
                  <option value="">-- Select Monster to Clone --</option>
                  {cloneSources().map(function (index) {
                    var m = state.monsters[index];
                    var name = m.customName ? m.customName : (m.selectedMonster ? m.selectedMonster : ('Monster ' + (index + 1)));
                    return <option key={index} value={'' + index}>{name + ' (Slot ' + (index + 1) + ')'}</option>;
                  })}
                </select>
              </div>

              <div className="vi-modal-field">
                <label className="vi-label-sm">How Many Copies?</label>
                <input
                  type="number"
                  id="cloneQuantity"
                  min="1"
                  max="10"
                  className="vi-modal-select-input"
                  value={cloneQuantity}
                  onChange={function (e) { setCloneQuantity(e.target.value); }}
                />
                <div className="vi-hint vi-mt-10">Creates up to available slots (max 16 total)</div>
              </div>

              <div className="vi-modal-actions">
                <button type="button" onClick={closeClone} className="vi-btn">Cancel</button>
                <button type="button" onClick={applyClone} className="btn-primary-gold vi-btn">Clone</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default EncounterView;
